/**
 * @whatItDoes This service acts on HTTP requests in the interceptor chain to handle a 401. See
 * https://angular.io/api/common/http/HttpInterceptor for more information on HTTP Interceptors
 *
 * TODO: Find out why some paths aren't reporting coverage in istanbul
 */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BugsnagService, HttpException } from '@uzurv/core';
import { AuthDataService, UZURV_REST_CONFIGURATION, UzurvRestConfiguration } from '@uzurv/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class HttpUnauthorizedInterceptor implements HttpInterceptor {
  constructor(
    private authDataService: AuthDataService,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    private bugsnagService: BugsnagService
  ) {}

  /**
   * Implementing intercept() takes in the request to be handled and if
   * an authentication token exists in localStorage, attaches it to the
   * headers in the correct field
   *
   * @param request the outgoing HttpRequest
   * @param next
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.shouldInterceptRequest(request)) {
      return next.handle(request);
    }

    /**
     * If handling the request produces 401: Unauthorized, then the
     * token isn't valid and we should clear it
     *
     * This is covered, but istanbul doesn't know that so...
     */
    /* istanbul ignore next */
    return next.handle(request).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse || err instanceof HttpException) {
          if (err.status === 401) {
            this.bugsnagService.leaveBreadcrumb('Calling authDataService.logout in 401 interceptor', {
              url: request.url,
              httpHeaders: request.headers
            });
            this.authDataService.clearAuthData();
          }
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Checks the request url to determine whether or not this interceptor should intercept
   *
   * @param request
   */
  private shouldInterceptRequest(request: HttpRequest<unknown>): boolean {
    if (
      !request.url.trim().startsWith(this.uzurvRestConfiguration.claraApiUrl) &&
      !request.url.trim().startsWith(this.uzurvRestConfiguration.dotsApiUrl) &&
      !request.url.trim().startsWith(this.uzurvRestConfiguration.reportingApiUrl)
    ) {
      return false;
    }

    // Login
    if (request.url.trim().endsWith('login')) {
      return false;
    }

    // Logout
    if (request.url.trim().endsWith('logout')) {
      return false;
    }

    // Application mode
    if (request.url.trim().endsWith('applicationModeCheck')) {
      return false;
    }

    return true;
  }
}
